import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload } from "lucide-react";

export default function AdminSettings() {
  const [s, setS] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/settings", s); toast.success("Saved"); } catch { toast.error("Failed"); }
    setBusy(false);
  };
  const uploadQR = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("folder", "settings");
    const r = await api.post("/files/upload", fd, { headers: {"Content-Type":"multipart/form-data"}});
    setS({...s, upi_qr_file_id: r.data.file_id});
    toast.success("QR uploaded");
  };
  const uploadAuctionQR = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("folder", "settings");
    const r = await api.post("/files/upload", fd, { headers: {"Content-Type":"multipart/form-data"}});
    setS({...s, auction_qr_file_id: r.data.file_id});
    toast.success("Auction QR uploaded");
  };
  const Field = ({ label, k, type = "text" }) => (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60">{label}</span>
      <input data-testid={`set-${k}`} type={type} value={s[k] ?? ""} onChange={(e) => setS({...s, [k]: type === "number" ? +e.target.value : e.target.value})} className="mt-2 w-full bg-black/50 border border-white/15 focus:border-[#39FF14] focus:outline-none px-3 py-2 text-sm"/>
    </label>
  );
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Configuration</div>
      <h1 className="font-display text-4xl mt-2">Settings</h1>
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div className="p-6 bg-[#0B132B] border border-white/10 rounded space-y-4">
          <h2 className="font-display text-2xl">League</h2>
          <Field label="Prize Pool (display)" k="prize_pool"/>
          <Field label="Registration Fee (₹)" k="registration_fee" type="number"/>
          <label className="flex items-center gap-3"><input type="checkbox" checked={!!s.registration_open} onChange={(e)=>setS({...s, registration_open: e.target.checked})} data-testid="set-reg-open"/><span className="text-sm">Registration Open</span></label>
          <Field label="Countdown Target (ISO date)" k="countdown_target"/>
          <Field label="Trial Date" k="trial_date"/>
          <Field label="Trial Venue" k="trial_venue"/>
        </div>
        <div className="p-6 bg-[#0B132B] border border-white/10 rounded space-y-4">
          <h2 className="font-display text-2xl">Payment</h2>
          <Field label="UPI ID" k="upi_id"/>
          <div>
            <div className="text-xs uppercase tracking-widest text-white/60 mb-2">NDPL Registration QR Image (₹799)</div>
            {s.upi_qr_file_id && <img src={fileUrl(s.upi_qr_file_id)} className="w-40 h-40 object-contain bg-white p-2 mb-2"/>}
            <label className="flex items-center gap-2 border border-dashed border-white/15 px-3 py-2 cursor-pointer text-xs" data-testid="upload-upi-qr"><Upload size={14} className="text-[#39FF14]"/>Upload Registration QR<input type="file" accept="image/*" className="hidden" onChange={(e)=>uploadQR(e.target.files?.[0])}/></label>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#FFD700] mb-2">HPL / NDPL Auction Payment QR (₹499)</div>
            {s.auction_qr_file_id && <img src={fileUrl(s.auction_qr_file_id)} className="w-40 h-40 object-contain bg-white p-2 mb-2" data-testid="auction-qr-preview"/>}
            <label className="flex items-center gap-2 border border-dashed border-[#FFD700]/40 px-3 py-2 cursor-pointer text-xs" data-testid="upload-auction-qr"><Upload size={14} className="text-[#FFD700]"/>Upload Auction QR<input type="file" accept="image/*" className="hidden" onChange={(e)=>uploadAuctionQR(e.target.files?.[0])}/></label>
            <div className="mt-1 text-[10px] text-white/40">Shown only on the HPL/NDPL Auction payment section. Separate from the ₹799 registration QR.</div>
          </div>
        </div>
        <div className="p-6 bg-[#0B132B] border border-white/10 rounded space-y-4">
          <h2 className="font-display text-2xl">Branding</h2>
          <div>
            <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Logo</div>
            {s.logo_file_id && <img src={fileUrl(s.logo_file_id)} className="w-24 h-24 object-cover rounded-full ring-1 ring-[#FFD700]/40 mb-2"/>}
            <label className="flex items-center gap-2 border border-dashed border-white/15 px-3 py-2 cursor-pointer text-xs"><Upload size={14} className="text-[#39FF14]"/>Upload Logo<input type="file" accept="image/*" className="hidden" onChange={async (e)=>{ const f=e.target.files?.[0]; if(!f) return; const fd=new FormData(); fd.append("file",f); fd.append("folder","branding"); const r=await api.post("/files/upload", fd, {headers:{"Content-Type":"multipart/form-data"}}); setS({...s, logo_file_id: r.data.file_id}); toast.success("Logo uploaded"); }}/></label>
          </div>
          <Field label="WhatsApp Number (E.164, digits only)" k="whatsapp_number"/>
        </div>
        <div className="p-6 bg-[#0B132B] border border-white/10 rounded space-y-4">
          <h2 className="font-display text-2xl">Home Page</h2>
          <Field label="Hero Title" k="home_hero_title"/>
          <Field label="Hero Subtitle" k="home_hero_subtitle"/>
          <label className="block"><span className="text-xs uppercase tracking-widest text-white/60">About NDPL</span>
            <textarea value={s.home_about ?? ""} onChange={(e)=>setS({...s, home_about: e.target.value})} rows="3" className="mt-2 w-full bg-black/50 border border-white/15 px-3 py-2 text-sm"/></label>
          <label className="block"><span className="text-xs uppercase tracking-widest text-white/60">League Vision</span>
            <textarea value={s.home_vision ?? ""} onChange={(e)=>setS({...s, home_vision: e.target.value})} rows="3" className="mt-2 w-full bg-black/50 border border-white/15 px-3 py-2 text-sm"/></label>
        </div>
        <div className="p-6 bg-[#0B132B] border border-white/10 rounded space-y-4">
          <h2 className="font-display text-2xl">Contact & Social</h2>
          <Field label="Contact Email" k="contact_email"/>
          <Field label="Contact Phone" k="contact_phone"/>
          <Field label="Instagram URL" k="social_instagram"/>
          <Field label="Facebook URL" k="social_facebook"/>
          <Field label="YouTube URL" k="social_youtube"/>
          <Field label="Twitter URL" k="social_twitter"/>
        </div>
      </div>
      <button onClick={save} disabled={busy} data-testid="save-settings" className="mt-6 px-8 py-3 bg-[#39FF14] text-black font-bold uppercase tracking-widest">{busy?"Saving…":"Save All Settings"}</button>
    </div>
  );
}
